import React, { useState } from 'react';
import { Lesson, Attachment } from '../../types';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  Spinner,
  ProgressBar
} from 'react-bootstrap';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaPlay,
  FaDownload,
  FaFile
} from 'react-icons/fa';
import { uploadLessonAttachment, deleteLessonAttachment } from '../../services/courseService';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';

interface LessonsListProps {
  courseId: string;
  lessons: Lesson[];
  loading?: boolean;
  error?: string | null;
  onLessonAdded: (lessonData: Partial<Lesson>) => Promise<void>;
  onLessonUpdated: (lessonId: string, lessonData: Partial<Lesson>) => Promise<void>;
  onLessonDeleted: (lessonId: string) => Promise<void>;
}

const LessonsList: React.FC<LessonsListProps> = ({
  courseId,
  lessons,
  loading = false,
  error = null,
  onLessonAdded,
  onLessonUpdated,
  onLessonDeleted
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Constants for file validation
  const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
    'application/zip',
    'application/x-zip-compressed'
  ];

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoSource, setVideoSource] = useState<'youtube' | 'upload'>('youtube');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [currentAttachments, setCurrentAttachments] = useState<Attachment[]>([]);

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setVideoUrl('');
    setVideoSource('youtube');
    setVideoFile(null);
    setAttachments([]);
    setCurrentAttachments([]);
    setSelectedLesson(null);
    setIsEditing(false);
    setShowModal(false);
    setUploadProgress(0);
    setUploadError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) return;

    try {
      setUploadProgress(0);
      setUploadError(null);
      
      let finalVideoUrl = videoUrl;
      
      // Handle video upload if selected
      if (videoSource === 'upload' && videoFile) {
        try {
          setUploadProgress(10);
          // Create a storage reference for the video
          const videoRef = ref(storage, `courses/${courseId}/videos/${Date.now()}_${videoFile.name}`);
          
          // Upload the video file
          const uploadTask = uploadBytesResumable(videoRef, videoFile);
          
          // Monitor upload progress
          uploadTask.on('state_changed', 
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 50; // 50% of total progress
              setUploadProgress(progress);
            },
            (error) => {
              console.error('Error uploading video:', error);
              setUploadError('حدث خطأ أثناء رفع الفيديو');
              return;
            }
          );
          
          // Wait for upload to complete
          await uploadTask;
          
          // Get the download URL
          finalVideoUrl = await getDownloadURL(videoRef);
          setUploadProgress(60);
        } catch (error) {
          console.error('Error uploading video:', error);
          setUploadError('حدث خطأ أثناء رفع الفيديو');
          return;
        }
      }
      
      const lessonData: Partial<Lesson> = {
        title,
        description,
        videoUrl: finalVideoUrl,
        courseId,
        order: lessons.length + 1,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };

      if (isEditing && selectedLesson) {
        // Upload new attachments if any
        if (attachments.length > 0) {
          const uploadedAttachments = await Promise.all(
            attachments.map(async (file) => {
              const attachment = await uploadLessonAttachment(file, courseId, selectedLesson.id);
              setUploadProgress((prev) => prev + (100 / attachments.length));
              return attachment;
            })
          );

          lessonData.attachments = [...(currentAttachments || []), ...uploadedAttachments];
        } else {
          lessonData.attachments = currentAttachments;
        }

        await onLessonUpdated(selectedLesson.id, lessonData);
      } else {
        // Create new lesson first to get the ID
        await onLessonAdded(lessonData);
        
        // After lesson is created, upload attachments if any
        if (attachments.length > 0) {
          const newLesson = lessons.find(l => l.title === title);
          if (newLesson) {
            const uploadedAttachments = await Promise.all(
              attachments.map(async (file) => {
                const attachment = await uploadLessonAttachment(file, courseId, newLesson.id);
                setUploadProgress((prev) => prev + (100 / attachments.length));
                return attachment;
              })
            );

            await onLessonUpdated(newLesson.id, {
              attachments: uploadedAttachments
            });
          }
        }
      }

      resetForm();
    } catch (error) {
      console.error('Error with lesson:', error);
      setUploadError('حدث خطأ أثناء رفع الملفات');
    }
  };

  const handleDelete = async () => {
    if (!selectedLesson) return;

    try {
      // Delete attachments first if any
      if (selectedLesson.attachments?.length) {
        await Promise.all(
          selectedLesson.attachments.map(attachment =>
            deleteLessonAttachment(courseId, selectedLesson.id, attachment.name)
          )
        );
      }

      await onLessonDeleted(selectedLesson.id);
      setShowDeleteModal(false);
      setSelectedLesson(null);
    } catch (error) {
      console.error('Error deleting lesson:', error);
      setUploadError('حدث خطأ أثناء حذف الدرس');
    }
  };

  const validateFiles = (files: File[]): { valid: boolean; error?: string } => {
    for (const file of files) {
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        return {
          valid: false,
          error: `الملف ${file.name} أكبر من الحد المسموح به (50 ميجابايت)`
        };
      }

      // Check file type
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        return {
          valid: false,
          error: `نوع الملف ${file.name} غير مسموح به`
        };
      }
    }

    return { valid: true };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = (e.target as HTMLInputElement).files;
    if (fileList) {
      const filesArray = Array.from(fileList);
      const validation = validateFiles(filesArray);
      
      if (!validation.valid) {
        setUploadError(validation.error || 'خطأ في الملفات المختارة');
        (e.target as HTMLInputElement).value = ''; // Clear the file input
        return;
      }

      setUploadError(null);
      setAttachments(filesArray);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    if (!selectedLesson) return;

    try {
      await deleteLessonAttachment(courseId, selectedLesson.id, attachment.name);
      const updatedAttachments = currentAttachments.filter(a => a.name !== attachment.name);
      setCurrentAttachments(updatedAttachments);
      await onLessonUpdated(selectedLesson.id, {
        attachments: updatedAttachments
      });
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setUploadError('حدث خطأ أثناء حذف المرفق');
    }
  };

  if (loading) {
    return (
      <div className="text-center my-4">
        <Spinner animation="border" />
        <p className="mt-2">جاري تحميل الدروس...</p>
      </div>
    );
  }

  return (
    <div className="lessons-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">الدروس ({lessons.length})</h4>
        <Button variant="primary" onClick={() => setShowModal(true)}>
          <FaPlus className="me-1" /> إضافة درس
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {lessons.length === 0 ? (
        <Alert variant="info">
          لا توجد دروس بعد. قم بإضافة درس جديد.
        </Alert>
      ) : (
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>#</th>
              <th>عنوان الدرس</th>
              <th>الوصف</th>
              <th>الفيديو</th>
              <th>المرفقات</th>
              <th>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {lessons.map((lesson, index) => (
              <tr key={lesson.id}>
                <td>{index + 1}</td>
                <td>{lesson.title}</td>
                <td>{lesson.description}</td>
                <td>
                  {lesson.videoUrl && (
                    <Button
                      variant="link"
                      size="sm"
                      href={lesson.videoUrl}
                      target="_blank"
                    >
                      <FaPlay /> مشاهدة
                    </Button>
                  )}
                </td>
                <td>
                  {lesson.attachments?.map((attachment, i) => (
                    <Button
                      key={i}
                      variant="link"
                      size="sm"
                      href={attachment.url}
                      target="_blank"
                      className="me-2"
                    >
                      <FaFile /> {attachment.name}
                    </Button>
                  ))}
                </td>
                <td>
                  <div className="d-flex gap-2">
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => {
                        setSelectedLesson(lesson);
                        setTitle(lesson.title);
                        setDescription(lesson.description || '');
                        setVideoUrl(lesson.videoUrl || '');
                        setCurrentAttachments(lesson.attachments || []);
                        setIsEditing(true);
                        setShowModal(true);
                      }}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => {
                        setSelectedLesson(lesson);
                        setShowDeleteModal(true);
                      }}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Add/Edit Lesson Modal */}
      <Modal show={showModal} onHide={resetForm} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'تعديل الدرس' : 'إضافة درس جديد'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>عنوان الدرس</Form.Label>
              <Form.Control
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>وصف الدرس</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>مصدر الفيديو</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="يوتيوب"
                  name="videoSource"
                  id="youtube"
                  checked={videoSource === 'youtube'}
                  onChange={() => setVideoSource('youtube')}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="رفع فيديو"
                  name="videoSource"
                  id="upload"
                  checked={videoSource === 'upload'}
                  onChange={() => setVideoSource('upload')}
                />
              </div>
            </Form.Group>

            {videoSource === 'youtube' ? (
              <Form.Group className="mb-3">
                <Form.Label>رابط الفيديو (يوتيوب)</Form.Label>
                <Form.Control
                  type="url"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                />
                <Form.Text className="text-muted">
                  أدخل رابط فيديو يوتيوب
                </Form.Text>
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>رفع فيديو</Form.Label>
                <Form.Control
                  type="file"
                  accept="video/mp4,video/webm,video/ogg"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (e.target.files && e.target.files[0]) {
                      const file = e.target.files[0];
                      if (file.size > 500 * 1024 * 1024) { // 500MB limit
                        setUploadError('حجم الفيديو يجب أن يكون أقل من 500 ميجابايت');
                        e.target.value = '';
                        return;
                      }
                      setVideoFile(file);
                      setUploadError(null);
                    }
                  }}
                />
                <Form.Text className="text-muted">
                  الحد الأقصى لحجم الفيديو: 500 ميجابايت. الأنواع المسموح بها: MP4, WebM, OGG
                </Form.Text>
                {videoFile && (
                  <div className="mt-2">
                    <span className="badge bg-info">
                      {videoFile.name} ({formatFileSize(videoFile.size)})
                    </span>
                  </div>
                )}
              </Form.Group>
            )}

            {isEditing && currentAttachments.length > 0 && (
              <Form.Group className="mb-3">
                <Form.Label>المرفقات الحالية</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {currentAttachments.map((attachment, index) => (
                    <div key={index} className="d-flex align-items-center border rounded p-2">
                      <Button
                        variant="link"
                        size="sm"
                        href={attachment.url}
                        target="_blank"
                        className="text-decoration-none"
                      >
                        <FaFile className="me-1" />
                        <span className="me-2">{attachment.name}</span>
                        <small className="text-muted">({formatFileSize(attachment.size || 0)})</small>
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="text-danger"
                        onClick={() => handleDeleteAttachment(attachment)}
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  ))}
                </div>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>إضافة مرفقات جديدة</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleFileChange}
                accept={ALLOWED_FILE_TYPES.join(',')}
              />
              <Form.Text className="text-muted">
                الحد الأقصى لحجم الملف: 50 ميجابايت. الأنواع المسموح بها: PDF, Word, Excel, PowerPoint, Images, Text, ZIP
              </Form.Text>
            </Form.Group>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <ProgressBar
                now={uploadProgress}
                label={`${Math.round(uploadProgress)}%`}
                className="mb-3"
              />
            )}

            {uploadError && (
              <Alert variant="danger" className="mb-3">
                {uploadError}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={resetForm}>
            إلغاء
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {isEditing ? 'تحديث' : 'إضافة'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>تأكيد الحذف</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>هل أنت متأكد من حذف هذا الدرس؟</p>
          {selectedLesson?.attachments && selectedLesson.attachments.length > 0 && (
            <Alert variant="warning">
              سيتم حذف جميع المرفقات المرتبطة بهذا الدرس أيضاً
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            حذف
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LessonsList;
